/**
 * The rules for acting on many locations at once (#1039).
 *
 * React-free so the dangerous parts can be tested exhaustively. The danger is
 * not the writing — the existing `POST /scheduling/test-due-visits` engine does
 * that, one site at a time, and this feature is a new CALLER of it rather than
 * a second path that creates jobs. The danger is the SELECTION.
 *
 * ── "Select all" is one keystroke from an incident ────────────────────────
 *
 * On a 1,088-location account, a select-all that silently means "all 1,088" is
 * how somebody creates a thousand jobs by accident. So:
 *
 *   - select-all applies to the CURRENT FILTER and the loaded page, never to
 *     an unbounded server-side set;
 *   - the bound is stated in words on screen, not implied;
 *   - and there is a hard cap that refuses rather than truncates. Truncating
 *     silently would be worse than the incident: the office would believe all
 *     of them were done.
 */

/** Mirrors the server's `MAX_BATCH_SITES`. Refuses past this; never truncates. */
export const MAX_BATCH_SITES = 50;

export interface BatchPreviewRow {
    site_id: string;
    site_name: string | null;
    device_count: number;
    skip_reason: string | null;
    existing_open_job_id: string | null;
}

export interface BatchSummary {
    /** Sites that would actually produce a job. */
    willCreate: number;
    /** Devices those jobs would carry in total. */
    deviceCount: number;
    /** Sites that would be skipped, and why — never silently dropped. */
    skipped: BatchPreviewRow[];
}

export function summarizeBatch(rows: readonly BatchPreviewRow[]): BatchSummary {
    const actionable = rows.filter((r) => r.skip_reason === null);
    return {
        willCreate: actionable.length,
        deviceCount: actionable.reduce((n, r) => n + r.device_count, 0),
        skipped: rows.filter((r) => r.skip_reason !== null),
    };
}

/**
 * The sentence on the confirm button's neighbourhood.
 *
 * States what WILL happen and what will NOT, in that order, always with
 * numbers. "Create 31 jobs" over a selection of 40 is a lie by omission.
 */
export function batchConfirmSentence(summary: BatchSummary): string {
    if (summary.willCreate === 0) {
        return "Nothing to create — every selected location is already covered or has nothing due.";
    }
    const jobs = `${summary.willCreate} job${summary.willCreate === 1 ? "" : "s"}`;
    const devices = `${summary.deviceCount} device${summary.deviceCount === 1 ? "" : "s"}`;
    const base = `Create ${jobs} covering ${devices}.`;
    if (summary.skipped.length === 0) return base;
    return `${base} ${summary.skipped.length} selected location${
        summary.skipped.length === 1 ? "" : "s"
    } will be skipped — see below.`;
}

export type SelectionError = { code: "too_many"; message: string } | null;

/**
 * Whether this selection may be acted on.
 *
 * Refuses past the cap rather than taking the first N. A silent truncation
 * reads as "all of them were done", which is the failure mode that turns a
 * bad click into a bad week.
 */
export function validateSelection(selected: readonly string[]): SelectionError {
    if (selected.length > MAX_BATCH_SITES) {
        return {
            code: "too_many",
            message:
                `${selected.length} locations selected. One batch is capped at ${MAX_BATCH_SITES} ` +
                `so a mis-click can't become a thousand jobs. Narrow the filter, or work through it in batches.`,
        };
    }
    return null;
}

/** What a select-all checkbox actually covers, said out loud. */
export function selectAllScopeLabel(visibleCount: number, bucketLabel: string, totalInBucket: number): string {
    if (visibleCount >= totalInBucket) {
        return `Selects all ${visibleCount} location${visibleCount === 1 ? "" : "s"} in ${bucketLabel}.`;
    }
    // The number on screen is not the number in the bucket. Saying only
    // "select all" here would be the unbounded-selection bug in words.
    return (
        `Selects the ${visibleCount} location${visibleCount === 1 ? "" : "s"} loaded below, ` +
        `not all ${totalInBucket} in ${bucketLabel}.`
    );
}

export type RowOutcome =
    | { status: "pending" }
    | { status: "creating" }
    | { status: "created"; jobId: string; assetCount: number }
    | { status: "skipped"; reason: string }
    | { status: "failed"; message: string };

export interface BatchResultSummary {
    created: number;
    failed: number;
    skipped: number;
    /** True while anything is still in flight. */
    running: boolean;
}

export function summarizeOutcomes(outcomes: ReadonlyMap<string, RowOutcome>): BatchResultSummary {
    let created = 0;
    let failed = 0;
    let skipped = 0;
    let running = false;
    for (const o of outcomes.values()) {
        if (o.status === "created") created += 1;
        else if (o.status === "failed") failed += 1;
        else if (o.status === "skipped") skipped += 1;
        else running = true;
    }
    return { created, failed, skipped, running };
}

/**
 * The result sentence.
 *
 * A partial failure is never reported as success — AGENTS.md's no-silent-
 * success rule. "40 created" when seven threw is the single most damaging
 * thing this feature could say.
 */
export function batchResultSentence(s: BatchResultSummary): string {
    if (s.running) return "Working…";
    const parts: string[] = [`${s.created} job${s.created === 1 ? "" : "s"} created`];
    if (s.failed > 0) parts.push(`${s.failed} failed`);
    if (s.skipped > 0) parts.push(`${s.skipped} skipped`);
    return `${parts.join(" · ")}.`;
}

/** Whether the run finished cleanly, for choosing the toast tone. */
export function batchSucceededCleanly(s: BatchResultSummary): boolean {
    return !s.running && s.failed === 0;
}
